import { useEffect, useId, useRef, useState } from 'react'
import { usePopoverDismiss } from '../usePopoverDismiss'
import { matchesQuery } from '../brandSelection'
import { displaySegment } from '../segments'
import { articleTitle, brandLabel, isUnnamed, STRENGTH_INFO, strengthText, UNNAMED_ARTICLE_INFO, type KitContents, type PackVariant } from '../missingLabels'
import { NO_SALES_IN_PERIOD } from '../filterSales'
import { InfoTip } from './InfoTip'

// kit (MP-OS-150): a sold vape kit, labelled "<kit name> · kit (contents unknown)" ("kit (volume unknown)" with kit_contents, MPD-141); kit_contents
// (MPD-135) its parts, shown in an ⓘ. strength_label (MPD-135): a vape article's strength as labelled.
export type Article = { sku: string; name: string; brand: string; segment: string; kit?: boolean; pack_variant?: PackVariant | null
  kit_contents?: KitContents | null; strength_label?: string | null }
// The segment is shown with its English display label (MPD-72); `segment` itself stays the API value.
// A missing name, SKU or brand is named by the shared labels (MP-OS-197).
export const articleLabel = (a: Article) => [articleTitle(a.sku, a.name, a.kit, a.pack_variant, a.kit_contents), strengthText(a.strength_label, a.segment),
  brandLabel(a.brand), a.segment && displaySegment(a.segment)].filter(Boolean).join(' · ')
export const articleShortLabel = (a: Article) => articleTitle(a.sku, a.name, a.kit, a.pack_variant, a.kit_contents)

export const NO_SALES_TAG = NO_SALES_IN_PERIOD
const NO_SALES_TIP = 'This article has no recorded sales in the selected period; it stays searchable.'

// `sold` (MP-OS-208): the SKUs with sales in the selected period; unknown (loading/failed) → no tags.
// It only tags suggestions: matching, order and the list itself stay as they are (MP-OS-168).
export function ArticlePicker({ label, articles, onSelect, selected, disabled = false, sold }: {
  label: string; articles: Article[]; onSelect: (article: Article) => void; selected?: Article; disabled?: boolean
  sold?: ReadonlySet<string> | null
}) {
  const id = useId()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const results = articles.filter(a => matchesQuery(`${a.sku} ${a.name} ${a.brand}`, query))
  const index = Math.min(active, results.length - 1)
  const expanded = open && !disabled
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  // The input is the trigger: Escape (from anywhere) closes the list and leaves focus there.
  const popover = usePopoverDismiss({ open, close: () => setOpen(false), triggerRef: inputRef, containerRef: wrapRef })
  useEffect(() => {
    if (expanded && index >= 0) document.getElementById(`${id}-${results[index].sku}`)?.scrollIntoView?.({ block: 'nearest' })
  }, [expanded, index, query, id])
  function select(article: Article) {
    onSelect(article)
    setQuery(''); setActive(0); setOpen(false)
  }
  return <div className="article-picker" ref={wrapRef} onBlur={e => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
  }}>
    <label htmlFor={id}>{label}</label>
    {articles.some(a => strengthText(a.strength_label, a.segment)) && <InfoTip label="About the strength" size="small">{STRENGTH_INFO}</InfoTip>}
    {/* MPD-185: "Unnamed article (<sku>)" is explained once, as the strength is. */}
    {(articles.some(a => isUnnamed(a.sku, a.name)) || (selected && isUnnamed(selected.sku, selected.name)))
      && <InfoTip label="About unnamed articles" size="small">{UNNAMED_ARTICLE_INFO}</InfoTip>}
    <div className="article-search">
      <input id={id} ref={inputRef} role="combobox" autoComplete="off" value={!open && selected ? articleLabel(selected) : query} disabled={disabled}
        aria-autocomplete="list" aria-expanded={expanded} aria-controls={`${id}-results`}
        aria-activedescendant={expanded && index >= 0 ? `${id}-${results[index].sku}` : undefined}
        placeholder="Search number, name or brand"
        onFocus={() => { if (!popover.isRestoringFocus()) setOpen(true) }} onClick={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setActive(0); setOpen(true) }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault(); setOpen(true)
            setActive(!expanded ? (e.key === 'ArrowDown' ? 0 : Math.max(0, results.length - 1))
              : Math.max(0, Math.min(results.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1))))
          }
          if (e.key === 'Enter' && expanded) {
            e.preventDefault()
            if (results[index]) select(results[index])
          }
        }} />
      {query && <button type="button" disabled={disabled} aria-label={`Clear ${label.toLowerCase()}`}
        onClick={() => { setQuery(''); setActive(0); setOpen(true); document.getElementById(id)?.focus() }}>Clear</button>}
    </div>
    {expanded && <>
      <ul id={`${id}-results`} role="listbox" aria-label={`${label} results`}>
        {results.map((a, i) => <li id={`${id}-${a.sku}`} key={a.sku} role="option" aria-selected={i === index}
          onMouseDown={e => e.preventDefault()} onMouseMove={() => setActive(i)} onClick={() => select(a)}>{articleLabel(a)}
          {sold && !sold.has(a.sku) && <span className="article-no-sales" title={NO_SALES_TIP}> {NO_SALES_TAG}</span>}</li>)}
      </ul>
      {!results.length && <p role="status">No matching articles.</p>}
    </>}
  </div>
}
